import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import type {
  AnswersImportPayload,
  CatalogueItem,
  LocalSession,
  Participant,
  ParticipantAnswer,
  RemoteCredentials,
  Seat,
  SharedNote,
  TakeLocalRoomOnlineOptions,
} from "./types";
import {
  clearLocalSession,
  createLocalSession,
  importLocalAnswers,
  loadLocalSession,
  saveLocalAnswer,
  saveLocalNote,
  saveLocalSession,
  THIRTY_DAYS_MS,
  setLocalSessionRetention,
} from "./lib/session";
import {
  createRemoteRoom,
  createRemoteRoomFromLocal,
  credentialsFromHash,
} from "./lib/remote";
import { useRemoteRoom } from "./hooks/useRemoteRoom";
import {
  Connecting,
  Landing,
  RemoteInvite,
  SessionSetup,
  TerminalRoom,
} from "./components/EntryFlow";
import { Board, type BoardActions } from "./components/Board";
import { PrivacySecurity } from "./components/PrivacySecurity";
import {
  clearRoomBrowserData,
  loadPartnerInvite,
  pruneExpiredPartnerInvites,
  savePartnerInvite,
} from "./lib/browserStorage";
import { togetherIsAvailableInBrowser } from "./lib/featureFlags";

type Screen = "landing" | "setup" | "invite" | "room";

export function App() {
  const togetherAvailable = togetherIsAvailableInBrowser();
  if (
    window.location.pathname === "/together" ||
    window.location.pathname.startsWith("/together/")
  ) {
    if (!togetherAvailable) return <UnavailableRoute />;
    return (
      <Suspense fallback={<Connecting message="Opening Together…" />}>
        <TogetherRoute />
      </Suspense>
    );
  }
  return <KinkExperience togetherAvailable={togetherAvailable} />;
}

const TogetherRoute = lazy(() => import("./together/TogetherRoute"));

function UnavailableRoute() {
  return (
    <main className="entry-shell">
      <header className="entry-header">
        <span className="wordmark">Feel Scene</span>
      </header>
      <section className="entry-hero">
        <h1>That page isn’t available.</h1>
        <a className="button primary" href="/">
          Back to Feel Scene
        </a>
      </section>
    </main>
  );
}

function KinkExperience({ togetherAvailable }: { togetherAvailable: boolean }) {
  const initialCredentials = useMemo(() => credentialsFromHash(), []);
  const [credentials, setCredentials] = useState<RemoteCredentials | null>(
    initialCredentials,
  );
  const [localSession, setLocalSession] = useState<LocalSession | null>(null);
  const [browserStorageReady, setBrowserStorageReady] = useState(false);
  const [screen, setScreen] = useState<Screen>(
    initialCredentials ? "room" : "landing",
  );
  const [pathname, setPathname] = useState(window.location.pathname);
  const [setupMode, setSetupMode] = useState<"local" | "remote">("remote");
  const [setupBusy, setSetupBusy] = useState(false);
  const [setupError, setSetupError] = useState("");
  const [pendingPartnerInvite, setPendingPartnerInvite] = useState<
    string | null
  >(null);
  const [pendingPartnerName, setPendingPartnerName] = useState("your partner");
  const remote = useRemoteRoom(credentials);
  const [savedPartnerInvite, setSavedPartnerInvite] = useState<string | null>(
    null,
  );

  useEffect(() => {
    let active = true;
    let pruneTimer: number | null = null;
    void loadLocalSession()
      .then((session) => {
        if (active) setLocalSession(session);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!active) return;
        setBrowserStorageReady(true);
        pruneTimer = window.setTimeout(() => {
          void pruneExpiredPartnerInvites();
        }, 0);
      });
    const onPopState = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => {
      active = false;
      if (pruneTimer !== null) window.clearTimeout(pruneTimer);
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  useEffect(() => {
    const roomId = remote.view?.roomId;
    if (!roomId) {
      setSavedPartnerInvite(null);
      return;
    }
    let active = true;
    void loadPartnerInvite(roomId)
      .then((invite) => {
        if (active) setSavedPartnerInvite(invite);
      })
      .catch(() => {
        if (active) setSavedPartnerInvite(null);
      });
    return () => {
      active = false;
    };
  }, [remote.view?.roomId]);

  useEffect(() => {
    if (!remote.view || !savedPartnerInvite) return;
    void savePartnerInvite(
      remote.view.roomId,
      savedPartnerInvite,
      remote.view.expiresAt,
    );
  }, [
    remote.view?.roomId,
    remote.view?.expiresAt,
    savedPartnerInvite,
  ]);

  const remoteEnded = Boolean(
    remote.view?.terminalState ||
      remote.error.includes("expired") ||
      remote.error.includes("deleted") ||
      remote.error.includes("ended"),
  );

  useEffect(() => {
    if (!credentials || !remoteEnded) return;
    void clearRoomBrowserData(credentials.roomId);
    if (window.location.hash) {
      history.replaceState(null, "", window.location.pathname);
    }
  }, [credentials?.roomId, pathname, remoteEnded]);

  const updateLocal = useCallback(
    (change: (session: LocalSession) => LocalSession) => {
      setLocalSession((current) => {
        if (!current) return current;
        const next = change(current);
        void saveLocalSession(next);
        return next;
      });
    },
    [],
  );

  const beginSetup = (mode: "local" | "remote") => {
    setSetupMode(mode);
    setSetupError("");
    setScreen("setup");
  };

  const submitSetup = async (participants: Record<Seat, Participant>) => {
    if (setupMode === "local") {
      const session = createLocalSession(participants);
      await saveLocalSession(session);
      setLocalSession(session);
      setScreen("room");
      return;
    }

    setSetupBusy(true);
    setSetupError("");
    try {
      const created = await createRemoteRoom(participants);
      await savePartnerInvite(
        created.credentials.roomId,
        created.partnerUrl,
        created.expiresAt,
      );
      history.replaceState(null, "", created.ownUrl);
      setCredentials(created.credentials);
      setPendingPartnerInvite(created.partnerUrl);
      setPendingPartnerName(participants.B.name);
      setScreen("invite");
    } catch (error) {
      setSetupError(
        error instanceof Error ? error.message : "The room could not be created.",
      );
    } finally {
      setSetupBusy(false);
    }
  };

  const goHome = () => {
    history.replaceState(null, "", location.pathname);
    setCredentials(null);
    setPendingPartnerInvite(null);
    setScreen("landing");
  };

  const takeLocalRoomOnline = async (
    options: TakeLocalRoomOnlineOptions,
  ) => {
    if (!localSession) {
      throw new Error("The local session is no longer available.");
    }
    const created = await createRemoteRoomFromLocal(localSession, options);
    await savePartnerInvite(
      created.credentials.roomId,
      created.partnerUrl,
      created.expiresAt,
    );
    try {
      localStorage.setItem(
        `feel-scene:position:${created.credentials.roomId}:A`,
        localSession.activeItemId,
      );
    } catch {
      // Position recovery is useful but must not block the online copy.
    }
    history.replaceState(null, "", created.ownUrl);
    setCredentials(created.credentials);
    setPendingPartnerInvite(created.partnerUrl);
    setPendingPartnerName(created.participants.B.name);
    setScreen("invite");
  };

  const showPrivacy = () => {
    const target = `/privacy-security${window.location.hash}`;
    history.pushState({ feelScenePrivacyReturn: true }, "", target);
    setPathname("/privacy-security");
  };

  const leavePrivacy = () => {
    if (history.state?.feelScenePrivacyReturn) {
      history.back();
      return;
    }
    const target = `/${window.location.hash}`;
    history.replaceState(null, "", target);
    setPathname("/");
  };

  if (pathname === "/privacy-security" || pathname === "/privacy-security/") {
    return (
      <PrivacySecurity
        returnLabel={
          credentials || screen === "room" ? "Back to room" : "Back to home"
        }
        onReturn={leavePrivacy}
      />
    );
  }

  if (screen === "landing") {
    if (!browserStorageReady) {
      return <Connecting message="Looking for a saved session on this device…" />;
    }
    return (
      <Landing
        resumable={localSession}
        togetherAvailable={togetherAvailable}
        onResume={() => setScreen("room")}
        onChoose={beginSetup}
        onPrivacy={showPrivacy}
      />
    );
  }

  if (screen === "setup") {
    return (
      <SessionSetup
        mode={setupMode}
        busy={setupBusy}
        error={setupError}
        onBack={() => setScreen("landing")}
        onSubmit={(participants) => void submitSetup(participants)}
      />
    );
  }

  if (screen === "invite" && credentials && pendingPartnerInvite) {
    return (
      <RemoteInvite
        partnerName={pendingPartnerName}
        partnerInvite={pendingPartnerInvite}
        onContinue={() => {
          setPendingPartnerInvite(null);
          setScreen("room");
        }}
      />
    );
  }

  if (credentials) {
    if (!remote.view) {
      if (remote.error.includes("expired") || remote.error.includes("ended")) {
        return <TerminalRoom message="This room has ended" onHome={goHome} />;
      }
      return <Connecting message={remote.error || "Opening your room…"} />;
    }
    if (remote.view.terminalState) {
      return (
        <TerminalRoom
          message={
            remote.view.terminalState === "expired"
              ? "This room has expired"
              : "This room was deleted"
          }
          onHome={goHome}
        />
      );
    }
    const actions: BoardActions = {
      saveAnswer: remote.saveAnswer,
      importAnswers: remote.importAnswers,
      saveNote: remote.saveNote,
      setActiveItem: remote.setActiveItem,
      updateProfile: remote.saveProfile,
      addCustom: remote.addCustom,
      updateCustom: remote.updateCustom,
      deleteCustom: remote.deleteCustom,
      deleteRoom: remote.deleteRoom,
      resetExpiry: remote.resetExpiry,
      clearError: () => remote.setError(""),
    };
    return (
      <Board
        session={remote.view}
        actions={actions}
        error={remote.error}
        partnerInvite={savedPartnerInvite}
        onShowPrivacy={showPrivacy}
      />
    );
  }

  if (!localSession) {
    setScreen("landing");
    return null;
  }

  const localActions: BoardActions = {
    saveAnswer: async (answer: ParticipantAnswer) => {
      updateLocal((session) =>
        saveLocalAnswer(session, session.activeSeat, answer),
      );
    },
    saveAnswerForSeat: async (seat, answer) => {
      updateLocal((session) => saveLocalAnswer(session, seat, answer));
    },
    importAnswers: async (payload: AnswersImportPayload) => {
      updateLocal((session) =>
        importLocalAnswers(
          session,
          "A",
          payload.answers,
          payload.customEntries,
        ),
      );
    },
    saveNote: async (note: SharedNote) => {
      updateLocal((session) => saveLocalNote(session, note));
    },
    setActiveItem: (itemId: string) => {
      updateLocal((session) => ({ ...session, activeItemId: itemId }));
    },
    updateProfile: async (participant: Participant) => {
      updateLocal((session) => ({
        ...session,
        participants: {
          ...session.participants,
          A: participant,
        },
      }));
    },
    addCustom: async (item: CatalogueItem) => {
      updateLocal((session) => ({
        ...session,
        customEntries: [...session.customEntries, item],
        activeItemId: item.id,
      }));
    },
    updateCustom: async (item: CatalogueItem) => {
      updateLocal((session) => ({
        ...session,
        customEntries: session.customEntries.map((entry) =>
          entry.id === item.id ? item : entry,
        ),
      }));
    },
    deleteCustom: (item: CatalogueItem) => {
      updateLocal((session) => ({
        ...session,
        customEntries: session.customEntries.filter(
          (entry) => entry.id !== item.id,
        ),
      }));
    },
    deleteRoom: () => {
      void clearRoomBrowserData(localSession.roomId);
      void clearLocalSession();
      setLocalSession(null);
      setScreen("landing");
    },
    resetExpiry: () => {
      updateLocal((session) => ({
        ...session,
        expiresAt: Date.now() + THIRTY_DAYS_MS,
      }));
      return true;
    },
    setLocalRetention: (retainedIndefinitely) => {
      updateLocal((session) =>
        setLocalSessionRetention(session, retainedIndefinitely),
      );
      return true;
    },
    clearError: () => undefined,
  };

  return (
    <Board
      session={localSession}
      actions={localActions}
      error=""
      partnerInvite={null}
      onTakeOnline={takeLocalRoomOnline}
      onShowPrivacy={showPrivacy}
    />
  );
}
